import { useEffect, useState, useCallback } from 'react'
import { createLogger } from '#app/utils/logger.ts'
import { useNavigatorProperty } from '#app/utils/navigator-permissions.ts'

const pushLogger = createLogger('UsePush', false, 'debug')

// BUILD_PHASE is build-time only, always false in browser runtime
const IS_BUILD_PHASE = typeof process !== 'undefined' && process.env?.BUILD_PHASE === 'true'

// Conditionally import Sentry only when not in build phase (lazy loaded)
let captureException: any = null

// Initialize Sentry lazily to avoid top-level await blocking module initialization
if (!IS_BUILD_PHASE && typeof window !== 'undefined') {
	import('@sentry/react').then((sentryModule) => {
		captureException = sentryModule.captureException
	}).catch(() => {
		console.warn('Sentry not available in app/hooks/use-push.ts')
	})
}

// debug tip: check `chrome://gcm-internals/` to see your push subscriptions
// look for TOO_MANY_REGISTRATIONS if you ran into a memory leak

export const VERIFY_SUBSCRIPTION_INTERVAL = 1000 * 30 // 30 seconds

/**
 * Interface for managing push notification subscriptions and permissions.
 * Provides a complete API for handling web push notifications in a browser environment.
 */
export type PushObject = {
	/**
	 * Whether the user is currently subscribed to push notifications.
	 * This is based on the presence of an active PushSubscription.
	 */
	isSubscribed: boolean

	/**
	 * The active push subscription containing endpoint and keys.
	 * Used for sending notifications to this specific browser/device.
	 * Null if the user is not subscribed.
	 */
	pushSubscription: PushSubscription | null

	/**
	 * Whether the user has granted permission for push notifications.
	 * Based on the browser's Notification.permission state.
	 * True only if permission is 'granted'.
	 */
	canSendPush: boolean

	/**
	 * Whether the initial subscription check is complete.
	 * Used to prevent race conditions in subscription management.
	 * True after the first service worker registration check.
	 */
	initialCheckComplete: boolean

	/**
	 * The current Notification.permission status: 'default', 'granted', or 'denied'.
	 * Useful for showing the exact browser permission state to the user.
	 */
	notificationPermission: NotificationPermission

	/**
	 * Request push notification permission from the browser.
	 * Shows the browser's native permission prompt to the user.
	 * @returns Promise resolving to 'granted', 'denied', or 'default'
	 */
	requestPermission: () => Promise<NotificationPermission>

	/**
	 * Subscribe to push notifications with the provided VAPID key.
	 * Creates a new push subscription in the service worker.
	 *
	 * @param publicKey - VAPID public key for push authentication
	 * @param callback - Optional success callback with the new subscription
	 * @param errorCallback - Optional error callback
	 */
	subscribeToPush: (
		publicKey: string,
		callback?: (subscription: PushSubscription) => void,
		errorCallback?: (error: unknown) => void,
	) => void

	/**
	 * Unsubscribe from push notifications.
	 * Removes the subscription from both browser and service worker.
	 *
	 * @param callback - Optional success callback
	 * @param errorCallback - Optional error callback
	 */
	unsubscribeFromPush: (
		callback?: () => void,
		errorCallback?: (error: unknown) => void,
	) => void
}

/**
 * Check if the browser supports push notifications
 */
function isPushSupported(): boolean {
	return typeof window !== 'undefined' && 'Notification' in window
}

/**
 * Get the current push notification permission state
 */
function getCurrentPermission(): NotificationPermission {
	if (!isPushSupported()) return 'denied'
	return window.Notification.permission
}

/**
 * Track service worker errors in Sentry
 */
function handleServiceWorkerError(error: unknown, action: string): void {
	pushLogger.error(`Service worker ${action} error:`, { error })
	if (captureException) {
		captureException(error, {
			tags: {
				type: 'service_worker',
				action,
			},
		})
	}
}

/**
 * Get the active service worker registration
 */
async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
	try {
		return (await navigator.serviceWorker.getRegistration()) ?? null
	} catch (err) {
		handleServiceWorkerError(err, 'get_registration')
		return null
	}
}

/**
 * React hook for managing push notification subscriptions and permissions.
 *
 * Features:
 * - Service worker registration and monitoring
 * - Push notification subscription management
 * - Permission handling
 * - Error tracking
 * - Initial state verification
 *
 * @param src - Source identifier for debugging
 * @returns PushObject with subscription state and management functions
 */
export const usePush = (): PushObject => {
	// Service worker availability check
	const { isAvailable: isServiceWorkerAvailable } =
		useNavigatorProperty('serviceWorker')

	// State Management
	const [swRegistration, setSWRegistration] =
		useState<ServiceWorkerRegistration | null>(null)
	const [isSubscribed, setIsSubscribed] = useState<boolean>(false)
	const [pushSubscription, setPushSubscription] =
		useState<PushSubscription | null>(null)
	const [canSendPush, setCanSendPush] = useState<boolean>(false)
	const [initialCheckComplete, setInitialCheckComplete] =
		useState<boolean>(false)
	const [notificationPermission, setNotificationPermission] =
		useState<NotificationPermission>(getCurrentPermission())

	// Memoized state update to prevent unnecessary re-renders
	const updateSubscriptionState = useCallback(
		(
			newIsSubscribed: boolean,
			newSubscription: PushSubscription | null,
			newCanSendPush: boolean,
		) => {
			setIsSubscribed(newIsSubscribed)
			setPushSubscription(newSubscription)
			setCanSendPush(newCanSendPush)
		},
		[],
	)

	/**
	 * Request push notification permission
	 */
	const requestPermission = async () => {
		if (!isServiceWorkerAvailable) return 'denied'
		if (canSendPush) return 'granted'
		if (!isPushSupported()) {
			setCanSendPush(false)
			return 'denied'
		}

		const permission = await window.Notification.requestPermission()
		setCanSendPush(permission === 'granted')
		setNotificationPermission(permission)
		return permission
	}

	/**
	 * Subscribe to push notifications
	 */
	const subscribeToPush = (
		publicKey: string,
		callback?: (subscription: PushSubscription) => void,
		errorCallback?: (error: unknown) => void,
	) => {
		if (!isServiceWorkerAvailable || !swRegistration?.pushManager) {
			pushLogger.log('push notifications not supported')
			errorCallback?.(new Error('Push notifications not supported'))
			return
		}

		swRegistration.pushManager
			.subscribe({
				userVisibleOnly: true,
				applicationServerKey: publicKey,
			})
			.then(
				(subscription) => {
					console.log('subscribed to push')
					setIsSubscribed(true)
					setPushSubscription(subscription)
					callback?.(subscription)
				},
				(error) => {
					errorCallback?.(error)
				},
			)
	}

	/**
	 * Unsubscribe from push notifications
	 */
	const unsubscribeFromPush = (
		callback?: () => void,
		errorCallback?: (error: unknown) => void,
	) => {
		if (!isServiceWorkerAvailable || !swRegistration?.pushManager) {
			errorCallback?.(new Error('Push notifications not supported'))
			return
		}

		swRegistration.pushManager
			.getSubscription()
			.then((subscription) => {
				if (subscription) {
					subscription.unsubscribe().then(
						() => {
							setIsSubscribed(false)
							setPushSubscription(null)
							callback?.()
						},
						(error) => {
							pushLogger.error('Error unsubscribing from push:', { error })
							errorCallback?.(error)
						},
					)
				}
			})
			.catch((error) => {
				pushLogger.error('Error getting push subscription:', { error })
				errorCallback?.(error)
			})
	}

	/**
	 * Effect: Initialize and monitor service worker registration
	 */
	useEffect(() => {
		if (typeof window === 'undefined' || !isServiceWorkerAvailable) return

		const setupServiceWorker = async () => {
			pushLogger.log('🛠️ Setting up service worker')
			const registration = await getServiceWorkerRegistration()
			if (!registration?.pushManager) return

			setSWRegistration(registration)

			// Check initial subscription state
			const subscription = await registration.pushManager.getSubscription()
			const newIsSubscribed = !!subscription
			const currentPermission = getCurrentPermission() === 'granted'

			updateSubscriptionState(newIsSubscribed, subscription, currentPermission)
			setInitialCheckComplete(true)
		}

		const handleControllerChange = async () => {
			pushLogger.log('👋 Service worker controller changed')
			await setupServiceWorker()
		}

		pushLogger.log('👋 Adding controllerchange listener')
		navigator.serviceWorker.addEventListener(
			'controllerchange',
			handleControllerChange,
		)
		void setupServiceWorker()

		return () => {
			navigator.serviceWorker.removeEventListener(
				'controllerchange',
				handleControllerChange,
			)
		}
	}, [isServiceWorkerAvailable])

	return {
		isSubscribed,
		canSendPush,
		initialCheckComplete,
		pushSubscription,
		requestPermission,
		subscribeToPush,
		unsubscribeFromPush,
		notificationPermission,
	}
}
